import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DuplicateConflictError, newIdempotencyKey, type DuplicateCandidate } from '../api/client'
import { customersApi, LEGAL_REP_ROLES, type CustomerCreateRequest, type LegalRepresentative } from '../api/customers'
import {
  FormGrid,
  Button,
  ButtonLink,
  Callout,
  Card,
  CardContent,
  CardHeader,
  LoadingState,
  Page as PageShell,
  PageHeader,
  Select,
  StatusPill,
  TextField,
} from '../components/ui'

/** API route segment for each `DuplicateCandidate.entityType` value the backend's
 * duplicate-detection service can return (see `DuplicateCheckModels.cs` /
 * `CustomerDuplicateDetectionService.cs`) — irregular plurals (Person → people,
 * Company → companies) so this can't be derived by lowercasing + 's'. */
const ENTITY_TYPE_API_SEGMENT: Record<string, string> = {
  Person: 'people',
  Company: 'companies',
  Contact: 'contacts',
  Customer: 'customers',
}

function candidateApiUrl(c: DuplicateCandidate): string {
  const segment = ENTITY_TYPE_API_SEGMENT[c.entityType] ?? c.entityType.toLowerCase()
  return `/api/${segment}/${c.id}`
}

/**
 * Maps the form's Italian `customerType` vocabulary to the domain's `CustomerType`
 * (`Customer.cs`) — TASK-0046 / RSK-024, widened from a lossy 2-way collapse to a full 6-way
 * mapping by TASK-0165 (REQ-004-002). The two vocabularies now line up 1:1: every Italian form
 * value has its own domain value, so nothing is collapsed any more.
 * `'professionista'` (freelance professional), previously collapsed onto `'person'` because "the
 * domain's binary discriminator has no third value", now maps to its own `'professional'` domain
 * value — that limitation is exactly what this task removes.
 *
 * Exported for `CustomersPage.tsx` (TASK-0065): its "Tipo" filter sends this same Italian
 * vocabulary and hits the exact same `Customer.CustomerType` column, so it reuses this function
 * rather than re-deriving a second mapping that could silently drift from this one. Note (out of
 * scope for TASK-0165, flagged for the Lead): that page's own `<select>` only offers the original
 * 3 Italian values, so it cannot yet filter on the 4 new domain values this function now supports.
 */
export function toDomainCustomerType(
  formType: string,
): 'person' | 'sole_proprietor' | 'professional' | 'company' | 'condominium' | 'other' {
  switch (formType) {
    case 'azienda':
      return 'company'
    case 'professionista':
      return 'professional'
    case 'ditta_individuale':
      return 'sole_proprietor'
    case 'condominio':
      return 'condominium'
    case 'altro':
      return 'other'
    default:
      return 'person'
  }
}

/**
 * The inverse mapping, used when loading an existing customer into the form (edit mode). No
 * longer lossy (TASK-0165): every domain value now has its own Italian form value, so a
 * `'professional'` customer loads back as `'professionista'` instead of collapsing into
 * `'persona_fisica'` as it did before this task.
 */
function toFormCustomerType(domainType: string | undefined): string {
  switch (domainType) {
    case 'company':
      return 'azienda'
    case 'professional':
      return 'professionista'
    case 'sole_proprietor':
      return 'ditta_individuale'
    case 'condominium':
      return 'condominio'
    case 'other':
      return 'altro'
    default:
      return 'persona_fisica'
  }
}

interface Props {
  mode: 'create' | 'edit'
}

interface FormState {
  customerType: string
  firstName: string
  lastName: string
  companyName: string
  email: string
  phone: string
  mobile: string
  address: string
  city: string
  postalCode: string
  province: string
  country: string
  fiscalCode: string
  vatNumber: string
  pec: string
  sdiCode: string
  notes: string
  birthDate: string
  birthCity: string
  birthProvince: string
}

type RepForm = Record<keyof LegalRepresentative, string>

const EMPTY_REP: RepForm = {
  firstName: '', lastName: '', fiscalCode: '', birthDate: '', birthCity: '', birthProvince: '',
  residenceStreet: '', residenceCivic: '', residenceZip: '', residenceCity: '', residenceProvince: '', role: '',
}

const NATURAL_FORM_TYPES = ['persona_fisica', 'ditta_individuale', 'professionista']
const REPRESENTED_FORM_TYPES = ['azienda', 'condominio']

const EMPTY_FORM: FormState = {
  customerType: 'persona_fisica',
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phone: '',
  mobile: '',
  address: '',
  city: '',
  postalCode: '',
  province: '',
  country: 'IT',
  fiscalCode: '',
  vatNumber: '',
  pec: '',
  sdiCode: '',
  notes: '',
  birthDate: '',
  birthCity: '',
  birthProvince: '',
}

export function CustomerFormPage({ mode }: Props) {
  const { id: idParam } = useParams<{ id: string }>()
  const id = parseInt(idParam ?? '0', 10)
  const navigate = useNavigate()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [rep, setRep] = useState<RepForm>(EMPTY_REP)
  const [repExisted, setRepExisted] = useState(false)
  const [saving, setSaving] = useState(false)
  // F-063: una chiave per tentativo di creazione; rigenerata dopo un successo.
  const idempotencyKey = useRef(newIdempotencyKey())
  const [error, setError] = useState<string | null>(null)
  const [duplicateConflict, setDuplicateConflict] = useState<
    { message: string; candidates: DuplicateCandidate[] } | null
  >(null)
  const [loadingData, setLoadingData] = useState(mode === 'edit')

  useEffect(() => {
    if (mode !== 'edit') return
    setLoadingData(true)
    customersApi
      .get(id)
      .then(customer => {
        setForm({
          customerType: toFormCustomerType(customer.customerType),
          firstName: customer.firstName ?? '',
          lastName: customer.lastName ?? '',
          companyName: customer.companyName ?? '',
          email: customer.email ?? '',
          phone: customer.phone ?? '',
          mobile: customer.mobile ?? '',
          address: customer.address ?? '',
          city: customer.city ?? '',
          postalCode: customer.postalCode ?? '',
          province: customer.province ?? '',
          country: customer.country ?? 'IT',
          fiscalCode: customer.fiscalCode ?? '',
          vatNumber: customer.vatNumber ?? '',
          pec: customer.pec ?? '',
          sdiCode: customer.sdiCode ?? '',
          notes: customer.notes ?? '',
          birthDate: customer.birthDate ?? '',
          birthCity: customer.birthCity ?? '',
          birthProvince: customer.birthProvince ?? '',
        })
        return customersApi.getLegalRepresentative(id).then(r => {
          if (!r.representative) return
          setRepExisted(true)
          const next = { ...EMPTY_REP }
          for (const k of Object.keys(next) as (keyof LegalRepresentative)[]) next[k] = r.representative[k] ?? ''
          setRep(next)
        })
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Errore nel caricamento'))
      .finally(() => setLoadingData(false))
  }, [mode, id])

  function setField(
    key: keyof FormState,
  ): (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void {
    return e => setForm(f => ({ ...f, [key]: e.target.value }))
  }

  function setRepField(
    key: keyof LegalRepresentative,
  ): (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void {
    return e => setRep(r => ({ ...r, [key]: e.target.value }))
  }

  async function handleSave(forceCreate = false): Promise<void> {
    setSaving(true)
    setError(null)
    if (!forceCreate) setDuplicateConflict(null)
    try {
      const body: CustomerCreateRequest = {
        customerType: toDomainCustomerType(form.customerType),
        firstName: form.firstName || undefined,
        lastName: form.lastName || undefined,
        companyName: form.companyName || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        mobile: form.mobile || undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        postalCode: form.postalCode || undefined,
        province: form.province || undefined,
        country: form.country || undefined,
        fiscalCode: form.fiscalCode || undefined,
        vatNumber: form.vatNumber || undefined,
        pec: form.pec || undefined,
        sdiCode: form.sdiCode || undefined,
        notes: form.notes || undefined,
      }
      if (NATURAL_FORM_TYPES.includes(form.customerType)) {
        body.birthDate = form.birthDate || null
        body.birthCity = form.birthCity || null
        body.birthProvince = form.birthProvince || null
      }

      let customerId: number
      if (mode === 'edit') {
        await customersApi.update(id, body)
        customerId = id
      } else {
        if (forceCreate) body.forceCreate = true
        const res = await customersApi.create(body, idempotencyKey.current)
        idempotencyKey.current = newIdempotencyKey()
        customerId = res.id
      }
      if (REPRESENTED_FORM_TYPES.includes(form.customerType)) {
        const filled = Object.values(rep).some(v => v.trim() !== '')
        try {
          if (filled) {
            const payload: LegalRepresentative = {}
            for (const k of Object.keys(rep) as (keyof LegalRepresentative)[]) payload[k] = rep[k].trim() || null
            await customersApi.putLegalRepresentative(customerId, payload)
          } else if (repExisted) {
            await customersApi.deleteLegalRepresentative(customerId)
          }
        } catch (repErr) {
          setError(
            `Cliente salvato, ma il rappresentante legale no: ${repErr instanceof Error ? repErr.message : 'errore'}. Riprova dalla modifica.`,
          )
          setSaving(false)
          if (mode === 'create') navigate('/customers/' + customerId + '/edit')
          return
        }
      }
      navigate('/customers/' + customerId)
    } catch (err) {
      if (err instanceof DuplicateConflictError) {
        setDuplicateConflict({ message: err.message, candidates: err.candidates })
        setSaving(false)
        return
      }
      setError(err instanceof Error ? err.message : 'Errore nel salvataggio')
      setSaving(false)
    }
  }

  if (loadingData) {
    return <LoadingState />
  }

  return (
    <PageShell>
      <PageHeader
        back={{ to: '/customers', label: 'Clienti' }}
        icon="person"
        title={mode === 'create' ? 'Nuovo Cliente' : 'Modifica Cliente'}
      />

      {duplicateConflict ? (
        <Callout tone="warning" icon="report" title={duplicateConflict.message}>
          <ul className="list-unstyled mb-3 ps-4">
            {duplicateConflict.candidates.map((c, i) => (
              <li key={`${c.entityType}-${c.id}-${i}`} className="mb-1">
                <StatusPill tone="neutral" className="me-2">{c.entityType}</StatusPill>
                ID {c.id} — campo corrispondente: <strong>{c.matchedField}</strong>
                {' '}
                <a
                  href={candidateApiUrl(c)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ms-2"
                >
                  Vedi record esistente
                </a>
                {(c.entityType === 'Customer' || c.entityType === 'Contact' || c.entityType === 'Person') && (
                  <Button
                    variant="outlined"
                    size="sm"
                    className="ms-2"
                    onClick={() =>
                      navigate(
                        c.entityType === 'Customer'
                          ? `/customers/${c.id}/edit`
                          : c.entityType === 'Person'
                            ? `/people/${c.id}/edit`
                            : `/contacts/${c.id}/edit`,
                      )
                    }
                  >
                    Usa questo record esistente
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <div className="d-flex gap-2">
            <Button
              variant="tonal"
              size="sm"
              disabled={saving}
              onClick={() => void handleSave(true)}
            >
              {saving ? 'Creazione...' : 'Crea comunque'}
            </Button>
            <Button variant="text" size="sm" disabled={saving} onClick={() => setDuplicateConflict(null)}>
              Annulla
            </Button>
          </div>
        </Callout>
      ) : (
        error && <Callout tone="error">{error}</Callout>
      )}

      {/* Informazioni Anagrafiche */}
      <Card variant="outlined">
        <CardHeader title="Informazioni anagrafiche" />
        <CardContent>
          <FormGrid columns={3}>
            <div>
              <Select
                label="Tipo Cliente *"
                id="cf-customerType"
                required
                value={form.customerType}
                onChange={setField('customerType')}
              >
                <option value="persona_fisica">Persona Fisica</option>
                <option value="ditta_individuale">Ditta Individuale</option>
                <option value="professionista">Professionista</option>
                <option value="azienda">Azienda</option>
                <option value="condominio">Condominio</option>
                <option value="altro">Altro</option>
              </Select>
            </div>
            <div>
              <TextField
                label="Nome"
                id="cf-firstName"
                type="text"
                value={form.firstName}
                onChange={setField('firstName')}
              />
            </div>
            <div>
              <TextField
                label="Cognome"
                id="cf-lastName"
                type="text"
                value={form.lastName}
                onChange={setField('lastName')}
              />
            </div>
            <div className="md-span-full">
              <TextField
                label={`Ragione Sociale / Azienda${form.customerType === 'azienda' ? ' *' : ''}`}
                id="cf-companyName"
                type="text"
                required={form.customerType === 'azienda'}
                value={form.companyName}
                onChange={setField('companyName')}
              />
            </div>
          </FormGrid>

          <FormGrid columns={3} className="mt-3">
            <div>
              <TextField
                label="Email"
                id="cf-email"
                type="email"
                value={form.email}
                onChange={setField('email')}
              />
            </div>
            <div>
              <TextField
                label="Telefono"
                id="cf-phone"
                type="text"
                value={form.phone}
                onChange={setField('phone')}
              />
            </div>
            <div>
              <TextField
                label="Mobile"
                id="cf-mobile"
                type="text"
                value={form.mobile}
                onChange={setField('mobile')}
              />
            </div>
          </FormGrid>

          <FormGrid columns={4} className="mt-3">
            <div className="md-span-2">
              <TextField
                label="Indirizzo"
                id="cf-address"
                type="text"
                value={form.address}
                onChange={setField('address')}
              />
            </div>
            <div>
              <TextField
                label="Città"
                id="cf-city"
                type="text"
                value={form.city}
                onChange={setField('city')}
              />
            </div>
            <div>
              <TextField
                label="CAP"
                id="cf-postalCode"
                type="text"
                value={form.postalCode}
                onChange={setField('postalCode')}
              />
            </div>
          </FormGrid>

          <FormGrid columns={4} className="mt-3">
            <div>
              <TextField
                label="Provincia"
                id="cf-province"
                type="text"
                maxLength={2}
                value={form.province}
                onChange={e => setForm(f => ({ ...f, province: e.target.value.toUpperCase() }))}
              />
            </div>
            <div>
              <TextField
                label="Nazione"
                id="cf-country"
                type="text"
                value={form.country}
                onChange={setField('country')}
              />
            </div>
          </FormGrid>
        </CardContent>
      </Card>

      {/* Dati di nascita (persona fisica, ditta individuale, professionista) */}
      {NATURAL_FORM_TYPES.includes(form.customerType) && (
        <Card variant="outlined">
          <CardHeader title="Dati di nascita" subtitle="Obbligatori nei contratti luce e gas" />
          <CardContent>
            <FormGrid columns={3}>
              <div>
                <TextField label="Data di nascita" id="cf-birthDate" type="date" value={form.birthDate} onChange={setField('birthDate')} />
              </div>
              <div>
                <TextField label="Luogo di nascita" id="cf-birthCity" type="text" value={form.birthCity} onChange={setField('birthCity')} />
              </div>
              <div>
                <TextField
                  label="Provincia di nascita"
                  id="cf-birthProvince"
                  type="text"
                  maxLength={2}
                  value={form.birthProvince}
                  onChange={e => setForm(f => ({ ...f, birthProvince: e.target.value.toUpperCase() }))}
                />
              </div>
            </FormGrid>
          </CardContent>
        </Card>
      )}

      {/* Rappresentante legale (azienda, condominio) */}
      {REPRESENTED_FORM_TYPES.includes(form.customerType) && (
        <Card variant="outlined">
          <CardHeader
            title={form.customerType === 'condominio' ? 'Amministratore / rappresentante legale' : 'Rappresentante legale'}
            subtitle="Firma i moduli del contratto. L'indirizzo del cliente resta quello della sede legale."
          />
          <CardContent>
            <FormGrid columns={3}>
              <div>
                <TextField label="Nome" id="lr-firstName" type="text" value={rep.firstName} onChange={setRepField('firstName')} />
              </div>
              <div>
                <TextField label="Cognome" id="lr-lastName" type="text" value={rep.lastName} onChange={setRepField('lastName')} />
              </div>
              <div>
                <TextField label="Codice fiscale" id="lr-fiscalCode" type="text" value={rep.fiscalCode} onChange={setRepField('fiscalCode')} />
              </div>
              <div>
                <TextField label="Data di nascita" id="lr-birthDate" type="date" value={rep.birthDate} onChange={setRepField('birthDate')} />
              </div>
              <div>
                <TextField label="Luogo di nascita" id="lr-birthCity" type="text" value={rep.birthCity} onChange={setRepField('birthCity')} />
              </div>
              <div>
                <TextField
                  label="Provincia di nascita"
                  id="lr-birthProvince"
                  type="text"
                  maxLength={2}
                  value={rep.birthProvince}
                  onChange={e => setRep(r => ({ ...r, birthProvince: e.target.value.toUpperCase() }))}
                />
              </div>
            </FormGrid>
            <FormGrid columns={4} className="mt-3">
              <div className="md-span-2">
                <TextField label="Residenza: via" id="lr-street" type="text" value={rep.residenceStreet} onChange={setRepField('residenceStreet')} />
              </div>
              <div>
                <TextField label="Civico" id="lr-civic" type="text" value={rep.residenceCivic} onChange={setRepField('residenceCivic')} />
              </div>
              <div>
                <TextField label="CAP" id="lr-zip" type="text" value={rep.residenceZip} onChange={setRepField('residenceZip')} />
              </div>
              <div className="md-span-2">
                <TextField label="Comune" id="lr-city" type="text" value={rep.residenceCity} onChange={setRepField('residenceCity')} />
              </div>
              <div>
                <TextField
                  label="Provincia"
                  id="lr-province"
                  type="text"
                  maxLength={2}
                  value={rep.residenceProvince}
                  onChange={e => setRep(r => ({ ...r, residenceProvince: e.target.value.toUpperCase() }))}
                />
              </div>
              <div>
                <Select label="In qualità di" id="lr-role" value={rep.role} onChange={setRepField('role')}>
                  <option value="">{form.customerType === 'condominio' ? 'Amministratore (predefinito)' : 'Legale rappresentante (predefinito)'}</option>
                  {LEGAL_REP_ROLES.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </Select>
              </div>
            </FormGrid>
          </CardContent>
        </Card>
      )}

      {/* Dati Fiscali */}
      <Card variant="outlined">
        <CardHeader title="Dati fiscali" />
        <CardContent>
          <FormGrid columns={3}>
            <div>
              <TextField
                label="Codice Fiscale"
                id="cf-fiscalCode"
                type="text"
                value={form.fiscalCode}
                onChange={setField('fiscalCode')}
              />
            </div>
            <div>
              <TextField
                label="P.IVA"
                id="cf-vatNumber"
                type="text"
                value={form.vatNumber}
                onChange={setField('vatNumber')}
              />
            </div>
            <div>
              <TextField
                label="PEC"
                id="cf-pec"
                type="email"
                value={form.pec}
                onChange={setField('pec')}
              />
            </div>
            <div>
              <TextField
                label="Codice SDI"
                id="cf-sdiCode"
                type="text"
                value={form.sdiCode}
                onChange={setField('sdiCode')}
              />
            </div>
          </FormGrid>
        </CardContent>
      </Card>

      {/* Note */}
      <Card variant="outlined">
        <CardHeader title="Note" />
        <CardContent>
          <TextField
            label="Note"
            labelHidden
            multiline
            id="cf-notes"
            rows={3}
            value={form.notes}
            onChange={setField('notes')}
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="d-flex gap-2">
        <Button
          variant="filled"
          icon="save"
          loading={saving}
          disabled={saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
        <ButtonLink to="/customers" variant="text">
          Annulla
        </ButtonLink>
      </div>
    </PageShell>
  )
}
